import { useCallback, useEffect, useState } from 'react';

type Unwrappable<T> = { unwrap: () => Promise<T> };

type Args = {
    fetchPage: (page: number) => Unwrappable<unknown> | void;
    isLast: boolean | undefined;
};

/**
 * Shared scaffolding for the dashboard's "load more" feed tabs.
 *
 * The caller owns: the lazy RTK query, building the args object (memoized so
 * `fetchPage` is stable), and passing the shown result's `last` flag as `isLast`.
 *
 * The hook owns: the 0-based page index, kicking off the initial fetch (and
 * resetting to page 0 when `fetchPage` identity changes — i.e. its closed-over
 * deps changed), and the load-more callback that advances `pageIndex` only
 * after the network call resolves, and only while the `fetchPage` that fired
 * it is still the current one.
 */
export const useLoadMoreFeed = ({ fetchPage, isLast }: Args) => {
    // pageIndex is kept with the fetchPage it belongs to, and reset to 0 when fetchPage identity
    // changes (its closed-over deps changed) via render-time prev-value compare
    // (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    // The initial fetch is still kicked off from an effect, but no setState occurs there.
    const [state, setState] = useState(() => ({ fetchPage, pageIndex: 0 }));
    if (fetchPage !== state.fetchPage) {
        setState({ fetchPage, pageIndex: 0 });
    }
    const { pageIndex } = state;

    useEffect(() => {
        fetchPage(0);
    }, [fetchPage]);

    const handleLoadMore = useCallback(() => {
        if (isLast) return;
        const next = pageIndex + 1;
        const result = fetchPage(next);
        if (!result) return;
        result
            .unwrap()
            // A reply for a feed that has since been replaced must not move the new feed's page.
            .then(() => setState((prev) => (prev.fetchPage === fetchPage ? { fetchPage, pageIndex: next } : prev)))
            .catch(() => {
                // Failures are surfaced via the caller's `isFetching` / error state;
                // swallow here to avoid an unhandled rejection in the console.
            });
    }, [isLast, fetchPage, pageIndex]);

    return { pageIndex, handleLoadMore };
};
